import React from "react";
import { Button, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

interface ScaleAnswerProps {
  disabled?: boolean;
  value?: number | null;
  min?: number;
  max?: number;
  step?: number;
  variant?: "nps" | "rating";
  minLabel?: string;
  maxLabel?: string;
  onChange?: (value: number) => void;
}

const getScaleValues = (min = 0, max = 10, step = 1) => {
  // Malformed persisted config (step 0/negative/NaN) would loop forever.
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || step <= 0) {
    return [];
  }
  const values: number[] = [];
  for (let value = min; value <= max; value += step) {
    values.push(value);
  }
  return values;
};

const isNpsScale = (variant?: "nps" | "rating", min?: number, max?: number) =>
  variant === "nps" || (variant === undefined && min === 0 && max === 10);

const getNpsCategoryKey = (value?: number | null, variant?: "nps" | "rating", min?: number, max?: number) => {
  if (value === undefined || value === null || !isNpsScale(variant, min, max)) {
    return null;
  }
  if (value <= 6) {
    return "npsCategory.critic";
  }
  if (value <= 8) {
    return "npsCategory.neutral";
  }
  return "npsCategory.promoter";
};

const ScaleAnswer = ({ disabled, value, min, max, step, variant, minLabel, maxLabel, onChange }: ScaleAnswerProps) => {
  const { t } = useTranslation("surveys");
  const npsCategoryKey = getNpsCategoryKey(value, variant, min, max);

  return (
    <div>
      <Space wrap>
        {getScaleValues(min, max, step).map((option) => (
          <Button
            key={option}
            type={value === option ? "primary" : "default"}
            disabled={disabled}
            onClick={() => onChange?.(option)}
          >
            {option}
          </Button>
        ))}
      </Space>
      {npsCategoryKey && (
        <Typography.Text className="mt-2 block" type="secondary">
          {t("npsCategory.label")}: {t(npsCategoryKey)}
        </Typography.Text>
      )}
      {(minLabel || maxLabel) && (
        <div className="mt-2 flex justify-between text-gray-500">
          <Typography.Text type="secondary">{minLabel}</Typography.Text>
          <Typography.Text type="secondary">{maxLabel}</Typography.Text>
        </div>
      )}
    </div>
  );
};

export default ScaleAnswer;
